import { Box, Button, Card, Dialog, DialogContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import React, { FC, useEffect, useState } from 'react';
import { useDataProvider, useNotify, usePermissions } from 'react-admin';
import { Category, CategoryListState } from '../../types/categories';
import HeadTable from '../common/sortTable/sortTable';
import "./index.css"
import EditIcon from '@material-ui/icons/Edit';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import PaginationTable from '../common/paginationTable';
import { getFieldByTitle } from '../../helpers/sortByTitle';
import CreateCategory from './CreateCategory';
import EditCategory from './EditCategory';
import RemoveItem from '../common/Remove/RemoveItem';
import { checkPermission } from '../../helpers/checkPermissions';
import TablesSearch from '../common/tablesSearch/tablesSearch';
import DiscountFilter from '../fixedOdds/DiscountFilter';

interface ModalState {
    add: boolean,
    edit: boolean,
    delete: boolean,
    category: undefined | Category
}


const CategoryList: FC = () => {
    let { permissions } = usePermissions();
    const dataProvider = useDataProvider()
    const notify = useNotify()
    const [state, setState] = useState<CategoryListState>({
        categories: Array<Category>(),
        total: 0,
        page: 1,
        perPage: 10,
        field: '_id',
        order: 'asc',
        isCategory: true,
        isSub: false,
        sub: '',
        category: ''
    })

    const [modal, setModal] = useState<ModalState>({
        add: false,
        edit: false,
        delete: false,
        category: undefined
    })

    const [dropdown, setDropdown] = useState({
        category: false,
        sub: false,
        status: false
    });

    const [searchState, setSearchState] = useState({
        sub: '',
        category: '',
    })

    const [blockedFilter, setBlockedFilter] = useState({
        with: false,
        without: false
    })

    const [filter, setFilter] = useState({
        blocked: 'all',
    })

    useEffect(() => {
        getCategories(state.page, state.perPage, "_id", 'asc', searchState.category, filter.blocked)
    }, [])

    const handleToggle = (isSub: boolean) => {
        const isCategory = isSub ? false : true
        setSearchState({ sub: '', category: '' })
        if (isCategory) {
            getCategories(1, 10, "_id", 'asc', searchState.category, filter.blocked)
        } else {
            getSubCategories(1, 10, "_id", 'asc', searchState.sub, filter.blocked, searchState.category)
        }
    }

    const getCategories = async (
        page: number,
        perPage: number,
        field: string,
        order: string,
        title: string,
        filter: string,
    ) => {
        const category = await dataProvider.getCategories('prizeCategories', {
            filter: { searchTitle: title, isActive: filter },
            pagination: { page: page, perPage: perPage },
            sort: { field: field, order: order },
        }) as any;

        setState({
            ...state,
            categories: category.data,
            total: category.total,
            isCategory: true,
            isSub: false,
            page: page,
            perPage: perPage,
            field: field,
            order: order
        })
    }

    const getSubCategories = async (
        page: number,
        perPage: number,
        field: string,
        order: string,
        title: string,
        isActive: string,
        categoryTitle?: string,
    ) => {
        const category = await dataProvider.getCategories('subcategories', {
            filter: { title: title, categoryTitle: categoryTitle, isActive: isActive },
            pagination: { page: page, perPage: perPage },
            sort: { field: field, order: order },
        }) as any;
        setState({
            ...state,
            categories: category.data,
            total: category.total,
            isCategory: false,
            isSub: true,
            page: page,
            perPage: perPage,
            field: field,
            order: order
        })
    }

    const handleSort = (direction: string, field: string) => {
        field = getFieldByTitle(field)
        if (field === 'isBlocked') {
            field = 'isActive'
        }
        if (field === 'categorytitle' && !state.isSub) {
            field = 'title'
        }
        if (field === "subcategorytitle") {
            field = 'title'
        }
        state.isSub
            ? getSubCategories(state.page, state.perPage, field, direction, searchState.sub, filter.blocked, searchState.category)
            : getCategories(state.page, state.perPage, field, direction, searchState.category, filter.blocked)
    }

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
        state.isSub
            ? getSubCategories(newPage + 1, state.perPage, state.field, state.order, searchState.sub, filter.blocked, searchState.category)
            : getCategories(newPage + 1, state.perPage, state.field, state.order, searchState.category, filter.blocked)
    };

    const handleLastPage = () => {
        const page = Math.ceil(state.total / state.perPage)
        state.isSub
            ? getSubCategories(page, state.perPage, state.field, state.order, searchState.sub, filter.blocked, searchState.category)
            : getCategories(page, state.perPage, state.field, state.order, searchState.category, filter.blocked)
    }

    const handleFirstPage = () => {
        state.isSub
            ? getSubCategories(1, state.perPage, state.field, state.order, searchState.sub, filter.blocked, searchState.category)
            : getCategories(1, state.perPage, state.field, state.order, searchState.category, filter.blocked)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const perPage = parseInt(event.target.value, 10)
        state.isSub
            ? getSubCategories(1, perPage, state.field, state.order, searchState.sub, filter.blocked, searchState.category)
            : getCategories(1, perPage, state.field, state.order, searchState.category, filter.blocked)
    }

    const openModal = (name: string, category?: Category) => {
        setModal({ ...modal, [name]: true, category: category })
    }

    const closeModal = () => {
        setModal({ ...modal, add: false, edit: false, delete: false, category: undefined })
        getCategories(state.page, state.perPage, state.field, state.order, searchState.category, filter.blocked)
    }

    const filterDropdown = (title: string) => {
        const titleArray = title.toLowerCase().split("-");
        setDropdown({ ...dropdown, [titleArray[0]]: !dropdown[titleArray[0]] });
    };

    const applySearch = (field: string) => {
        state.isSub
            ? getSubCategories(1, state.perPage, state.field, state.order, searchState.sub, filter.blocked, searchState.category)
            : getCategories(1, state.perPage, state.field, state.order, searchState.category, filter.blocked)
    }

    const clearSearch = (clear: string) => {
        setSearchState({ ...searchState, [clear]: '' })
    }

    const changeStatus = async (item: Category) => {
        if (checkPermission(permissions, "Lifestyle Prizes", "Update")) {
            const result = state.isSub
                ? await dataProvider.update("subcategories/status",
                    {
                        data: item,
                        id: item._id,
                        previousData: {} as any
                    }
                )
                : await dataProvider.update("prizeCategories/status",
                    {
                        data: item,
                        id: item._id,
                        previousData: {} as any
                    }
                )
            if (result.data) {
                state.isSub
                    ? getSubCategories(state.page, state.perPage, state.field, state.order, searchState.sub, filter.blocked, searchState.category)
                    : getCategories(state.page, state.perPage, state.field, state.order, searchState.category, filter.blocked)
            }
        }
    }

    const removeCategory = async () => {
        if (checkPermission(permissions, "Lifestyle Prizes", "Update")) {
            try {
                const result = state.isSub
                    ? await dataProvider.delete("subcategories", {
                        id: modal.category ? modal.category._id : '',
                        previousData: {} as any
                    })
                    : await dataProvider.delete("prizeCategories", {
                        id: modal.category ? modal.category._id : '',
                        previousData: {} as any
                    })
                if (result.data) {
                    notify("Category has been deleted")
                    state.isSub
                        ? getSubCategories(state.page, state.perPage, state.field, state.order, searchState.sub, filter.blocked, searchState.category)
                        : getCategories(state.page, state.perPage, state.field, state.order, searchState.category, filter.blocked)
                    closeModal()
                }
            } catch (error) {
                notify(error.data.error, "error")
            }
        }
    }

    const search = (value: string, field: string) => {
        setSearchState({ ...searchState, [field]: value })
    }

    const checkBlockedFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
        const discount = blockedFilter
        discount[event.target.name] = event.target.checked
        let currentDiscount = ''
        if (discount.with && discount.without || !discount.with && !discount.without) {
            currentDiscount = 'all'
        }
        if (discount.with && !discount.without) {
            currentDiscount = 'with'
        }
        if (!discount.with && discount.without) {
            currentDiscount = 'without'
        }
        setFilter({ ...filter, blocked: currentDiscount })
        setBlockedFilter(discount)
    }

    const applyBlockedFilter = () => {
        let filter = ''
        if (blockedFilter.with && blockedFilter.without || !blockedFilter.with && !blockedFilter.without) {
            filter = 'all'
        }
        if (blockedFilter.with && !blockedFilter.without) {
            filter = 'with'
        }
        if (!blockedFilter.with && blockedFilter.without) {
            filter = 'without'
        }
        setFilter({ blocked: filter })
        state.isSub
            ? getSubCategories(state.page, state.perPage, state.field, state.order, searchState.sub, filter, searchState.category)
            : getCategories(state.page, state.perPage, state.field, state.order, searchState.category, filter)
    }

    const clearDiscountFilter = () => {
        setBlockedFilter({ with: false, without: false })
        setFilter({ ...filter, blocked: "all" })
    }

    return (
        <>
            <div className="dreamhome-main-title base-color">
                Prize Categories - Lifestyle Prizes
            </div>
            <Box display="flex" justifyContent="space-between" padding="2em">
                <div className='button-group'>
                    <Button className={state.isCategory ? "active-button" : "inactive-button"} onClick={() => handleToggle(false)}>Category</Button>
                    <Button className={state.isSub ? "active-button" : "inactive-button"} onClick={() => handleToggle(true)}>Sub-category</Button>
                </div>
                {
                    checkPermission(permissions, "Lifestyle Prizes", "Update")
                    &&
                    <Button className="active-button" onClick={() => (openModal("add"))}>Add {state.isCategory ? 'Category' : 'Sub-category'}
                    </Button>
                }
            </Box>
            <Box marginTop="0.5em">
                <Card>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {state.isSub &&
                                        <TableCell id="category-table-head">
                                            <HeadTable title="Sub-category" sort={handleSort} filterHandle={filterDropdown} />
                                            {dropdown.sub &&
                                                <TablesSearch
                                                    search={(event) => search(event.currentTarget.value, 'sub')}
                                                    applySearch={() => applySearch('sub')}
                                                    searchString={searchState.sub}
                                                    clearSearch={() => clearSearch('sub')}
                                                />}
                                        </TableCell>
                                    }
                                    <TableCell id="category-table-head">
                                        <HeadTable title="Category" sort={handleSort} filterHandle={filterDropdown} />
                                        {
                                            dropdown.category &&
                                            <TablesSearch
                                                search={(event) => search(event.currentTarget.value, 'category')}
                                                applySearch={() => applySearch('category')}
                                                searchString={searchState.category}
                                                clearSearch={() => clearSearch('category')}
                                            />
                                        }
                                    </TableCell>
                                    <TableCell id="picture-table-head">
                                        Picture
                                    </TableCell>
                                    <TableCell id="picture-table-head">
                                        <HeadTable title="Status" sort={handleSort} filterHandle={filterDropdown} />
                                        {
                                            dropdown.status
                                            &&
                                            <DiscountFilter
                                                checkDiscountFilter={checkBlockedFilter}
                                                applyFilter={applyBlockedFilter}
                                                clearSelectedItem={clearDiscountFilter}
                                                withDiscount={blockedFilter.with}
                                                withoutDiscount={blockedFilter.without}
                                                firstCheckboxTitle="active"
                                                secondCheckboxTitle="inactive"
                                            />
                                        }
                                    </TableCell>
                                    {
                                        (checkPermission(permissions, "Lifestyle Prizes", "Update") ||
                                            checkPermission(permissions, "Lifestyle Prizes", "Delete"))
                                        &&
                                        <TableCell id="picture-table-head">
                                            Actions
                                    </TableCell>
                                    }
                                </TableRow>
                            </TableHead>
                            {
                                state.categories.map((item) => {
                                    return (
                                        <TableBody key={item._id}>
                                            <TableRow>
                                                {
                                                    state.isSub &&
                                                    <TableCell id="category-row">
                                                        {item.title}
                                                    </TableCell>
                                                }
                                                <TableCell id="category-row">
                                                    {state.isSub ? item.categorytitle : item.title}
                                                </TableCell>
                                                <TableCell id="category-row">
                                                    <Box display="flex" alignItems="center" justifyContent="center">
                                                        <img className="category-img" src={`${process.env.REACT_APP_AWS_S3_URL}/prizePhotos/${item.photoUrlKey}.jpg`} alt="" />
                                                    </Box>
                                                </TableCell>
                                                <TableCell id="category-row">
                                                    <Box display="flex" justifyContent="center">
                                                        <label className="switch">
                                                            <input type="checkbox" checked={item.isActive} onChange={() => changeStatus(item)} />
                                                            <span className="slider round"></span>
                                                        </label>
                                                    </Box>
                                                </TableCell>
                                                {
                                                    (checkPermission(permissions, "Lifestyle Prizes", "Update") ||
                                                        checkPermission(permissions, "Lifestyle Prizes", "Delete")) &&
                                                    <TableCell id="category-row">
                                                        <div className="actions-table-body">
                                                            {checkPermission(permissions, "Lifestyle Prizes", "Update") && <EditIcon onClick={() => openModal("edit", item)} id="show-tickets" />}
                                                            {checkPermission(permissions, "Lifestyle Prizes", "Delete") && <DeleteForeverIcon className="delete-icon" onClick={() => openModal("delete", item)} />}
                                                        </div>
                                                    </TableCell>}
                                            </TableRow>
                                        </TableBody>
                                    )
                                })
                            }
                        </Table>
                    </TableContainer>
                </Card>
                <PaginationTable
                    page={state.page}
                    perPage={state.perPage}
                    collectionLength={state.categories.length}
                    total={state.total}
                    handleChangePage={handleChangePage}
                    handleLastPage={handleLastPage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    handleFirstPage={handleFirstPage}
                    title="Category"
                />
                <Dialog
                    fullWidth
                    open={modal.add}
                    onClose={closeModal}
                >
                    <DialogContent>
                        <CreateCategory
                            title={state.isSub ? "sub-category" : "category"}
                            closeModal={closeModal}
                            isSub={state.isSub}
                        />
                    </DialogContent>
                </Dialog>

                <Dialog
                    fullWidth
                    open={modal.edit}
                    onClose={closeModal}
                >
                    <DialogContent>
                        <EditCategory
                            parentState={state}
                            title={state.isSub ? "sub-category" : "category"}
                            refresh={state.isSub ? getSubCategories : getCategories}
                            closeModal={closeModal}
                            isSub={state.isSub}
                            category={modal.category}
                        />
                    </DialogContent>
                </Dialog>

                <Dialog
                    fullWidth
                    open={modal.delete}
                    onClose={closeModal}
                >
                    <DialogContent>
                        <RemoveItem
                            closeModal={closeModal}
                            removeUser={removeCategory}
                            minorText="You can delete a category only if it does not contain prizes"
                            mainText="Category"
                        />
                    </DialogContent>
                </Dialog>
            </Box>
        </>
    )
}

export default CategoryList